import { ExclamationTriangleIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiRequest } from "src/async/apiUtils";
import Section from "src/components/Shared/Containers/Section";
import Badge from "src/components/ServerMonitoring/Badge";
import { classNames } from "src/helpers/classNames";
import { formatDateTime } from "src/helpers/serverMonitorHelper";

const SSL_WARNING_DAYS = 14;
const phaseStyles = {
  ACTIVE: { label: "Active", className: "bg-green-100 text-green-700" },
  CONFIGURING: { label: "Configuring", className: "bg-amber-100 text-amber-700" },
  PENDING: { label: "Pending", className: "bg-amber-100 text-amber-700" },
  ERROR: { label: "Error", className: "bg-red-100 text-red-700" },
  NOT_FOUND: { label: "Not in DigitalOcean", className: "bg-red-100 text-red-700" },
};

const sslState = (expiresAt) => {
  if (!expiresAt) return null;
  const days = Math.floor((new Date(expiresAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000));
  if (days < 0) return { level: "expired", label: `Expired ${formatDateTime(expiresAt, "MMM D")}`, className: "text-red-700 font-semibold" };
  if (days <= SSL_WARNING_DAYS) return { level: "soon", label: `Expires in ${days} day${days === 1 ? "" : "s"}`, className: "text-amber-700 font-semibold" };
  return { level: "ok", label: formatDateTime(expiresAt, "MMM D, YYYY"), className: "text-gray-500" };
};

// A domain needs attention when it is not Active or its certificate has expired / expires soon.
const needsAttention = (domain) => domain.phase !== "ACTIVE" || ["expired", "soon"].includes(sslState(domain.certificate_expires_at)?.level);

const Count = ({ label, value, tone = "default" }) => (
  <div className="flex flex-col gap-1 rounded-md border border-gray-100 bg-gray-50/60 px-4 py-3">
    <span className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</span>
    <span className={classNames("text-lg font-semibold", tone === "bad" && value > 0 ? "text-red-600" : tone === "warn" && value > 0 ? "text-amber-600" : "text-gray-900")}>{value}</span>
  </div>
);

// One domain per client (its custom domain if set, otherwise its tenant domain), checked against the domains attached
// to the SaaS app in DigitalOcean. A domain that is not Active means that client's site is unreachable, even while the
// app itself is healthy.
const DomainHealth = ({ status }) => {
  const [domains, setDomains] = useState(null);
  const [counts, setCounts] = useState(null);
  const [search, setSearch] = useState("");
  const [problemsOnly, setProblemsOnly] = useState(true);
  const [error, setError] = useState(null);
  const checkedAt = status?.app?.checked_at;

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await apiRequest("get", "/server-monitor/domains");
      setDomains(res.data.data || []);
      setCounts(res.data.meta?.counts || null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  // Domain data is refreshed by the monitoring check; reload about every 5 minutes of checks.
  const refreshKey = checkedAt ? Math.floor(new Date(checkedAt).getTime() / (5 * 60 * 1000)) : 0;
  useEffect(() => {
    if (status?.configured) load();
  }, [load, status?.configured, refreshKey]);

  const problemCount = useMemo(() => (domains || []).filter(needsAttention).length, [domains]);
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (domains || []).filter(
      (domain) =>
        (!problemsOnly || needsAttention(domain)) &&
        (!needle ||
          domain.domain.includes(needle) ||
          String(domain.client?.name || "")
            .toLowerCase()
            .includes(needle)),
    );
  }, [domains, search, problemsOnly]);

  if (!status?.configured) return null;

  return (
    <Section shadow>
      <h2 className="text-xl font-bold text-gray-900">Domains</h2>
      <p className="mt-1 text-sm text-gray-500">Each client&apos;s domain - its custom domain if set, otherwise its tenant domain. A domain that is not Active is unreachable for that client, even while the app is healthy.</p>

      {counts && (
        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-6">
          <Count
            label="Total"
            value={counts.total}
          />
          <Count
            label="Active"
            value={counts.active}
          />
          <Count
            label="Configuring"
            value={counts.configuring}
            tone="warn"
          />
          <Count
            label="Error"
            value={counts.error}
            tone="bad"
          />
          <Count
            label="Not in DigitalOcean"
            value={counts.not_found}
            tone="bad"
          />
          <Count
            label="SSL expiring / expired"
            value={counts.ssl_expiring}
            tone="warn"
          />
        </div>
      )}

      {problemCount > 0 && (
        <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <span>
            <b>
              {problemCount} domain{problemCount === 1 ? " needs" : "s need"} attention.
            </b>{" "}
            Usually the client&apos;s DNS record (CNAME) is missing or points elsewhere - DigitalOcean cannot finish setup or renew the SSL certificate until it is fixed. &quot;Not in DigitalOcean&quot; means the domain was never added to the app.
          </span>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-xs">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search domain or client"
            className="h-9 w-full rounded-md border-gray-300 pl-8 text-sm focus:border-highlightColor focus:ring-0"
          />
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={problemsOnly}
            onChange={(e) => setProblemsOnly(e.target.checked)}
            className="rounded border-gray-300 text-highlightColor focus:ring-0"
          />
          Only domains that need attention
        </label>
      </div>

      {error && <p className="mt-3 text-sm text-amber-700">{error}</p>}
      {domains && (
        <div className="mt-3 max-h-[480px] overflow-auto rounded-md border border-gray-100">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
              <tr>
                <th className="px-4 py-2 font-medium">Domain</th>
                <th className="px-4 py-2 font-medium">Client</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">SSL certificate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {visible.length === 0 && (
                <tr>
                  <td
                    colSpan="4"
                    className="px-4 py-6 text-center text-sm text-gray-400">
                    {problemsOnly && !search ? "All domains are active and their certificates are valid." : "No matching domains."}
                  </td>
                </tr>
              )}
              {visible.map((domain) => {
                const style = phaseStyles[domain.phase] || { label: domain.phase, className: "bg-gray-100 text-gray-600" };
                const ssl = sslState(domain.certificate_expires_at);
                return (
                  <tr key={domain.domain}>
                    <td className="px-4 py-2 text-xs">
                      <div className="font-medium text-gray-900">{domain.domain}</div>
                      <div className="text-gray-400">{domain.type === "custom" ? "Custom domain" : "Tenant domain"}</div>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600">
                      {domain.client?.name || "Unnamed client"}
                      {domain.client?.status && domain.client.status !== "active" && <span className="text-gray-400"> ({domain.client.status})</span>}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <Badge
                        label={style.label}
                        className={style.className}
                      />
                      {domain.problem?.message && <div className="mt-1 max-w-sm text-amber-800">{domain.problem.message}</div>}
                    </td>
                    <td className={classNames("whitespace-nowrap px-4 py-2 text-xs", ssl?.className || "text-gray-400")}>{ssl?.label || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
};

export default DomainHealth;
